import React, { useRef, useState, useEffect, useCallback } from "react";

import { fabric } from "fabric";
import { toast } from "react-toastify";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import Utils from "../../../../utils";
import {
  getEnvelopeRequestSignDocumentPages,
  signPlaceholdersUpdate,
} from "../../../../services/CommonService";
import { EVERY_ONE_OBJECT, FONT_FAMILY_LIST, FONT_SIZE_LIST, DATEFORMAT_LIST } from "../../../../configs/AppConfig";
import ContractSummaryModal from './ContractSummaryModal';

function MakeFillablePopup({
  makeFillable,
  signatureDocumentData,
  setLoading,
  formObj,
  setFormObj,
  handleMakeFillable,
}) {
  const animatedComponents = makeAnimated();

  // --- States ---
  const [signDocId, setSignDocId] = useState(0);
  const [signDocMode, setSignDocMode] = useState(null);
  const [canvas, setCanvas] = useState("");
  const [imageList, setImageList] = useState([]);
  const [docRecipients, setDocRecipients] = useState([]);
  const [requestSign, setRequestSign] = useState([]);
  const [assignToRecipient, setAssignToRecipient] = useState([]);


  const [tempRecipient, setTempRecipient] = useState("");
  const [fileName, setFileName] = useState(null);
  const [draggedItemType, setDraggedItemType] = useState(null);
  const [noFieldSelectionFlag, setNoFieldSelectionFlag] = useState(true);
  const [signerSelectedBackgroundColor, setSignerSelectedBackgroundColor] = useState("d1ecf1");

  // Tool Properties States
  const [selectedFont, setSelectedFont] = useState("");
  const [selectedFontSize, setSelectedFontSize] = useState("");
  const [selectedDateFormat, setSelectedDateFormat] = useState("");
  const [selectedTextContent, setSelectedTextContent] = useState("");

  // Visibility States
  const [visibleFabricTools, setVisibleFabricTools] = useState(false);
  const [visibleDateFormatDropdown, setVisibleDateFormatDropdown] = useState(false);
  const [visibleTextContentTextarea, setVisibleTextContentTextarea] = useState(false);
  const [visibleAssignToDropdown, setVisibleAssignToDropdown] = useState(false);

  const [previewButtonText, setPreviewButtonText] = useState("Preview");
  const [fontLoaded, setFontLoaded] = useState(false);

  let errorsObj = { recipients: "", };
  const [errors, setErrors] = useState(errorsObj);
  // Refs
  const debounceTimeout = useRef(null);
  const canvasRef = useRef();
  const clsFormButtonRef = useRef();
  const containerRef = useRef();
  const autoFillEditModeRef = useRef();
  const offcanvasRef = useRef(null);






  // --- Fabric & Canvas Logic ---

  const handleDragStart = (e, type) => {
    setDraggedItemType(type);
  };

  const handleDragStop = (e) => {
    var mousePosition = localStorage.getItem("mousePosition");
    mousePosition = JSON.parse(mousePosition);

    if (mousePosition !== null) {
      let backgroundColor = signerSelectedBackgroundColor;
      let leftPositionObject = mousePosition.x - 75;
      let topPositionObject = mousePosition.y - 25;

      const widthObject = 150;
      const heightObject = 50;
      const objId = generateId(draggedItemType);
      const requestRecipient = tempRecipient;
      const fontSize = "14";
      const fontFamily = "Arial";
      const textContent = "";
      const reRenderRightSidebar = true;

      fabricTool(
        backgroundColor,
        topPositionObject,
        leftPositionObject,
        widthObject,
        heightObject,
        objId,
        requestRecipient,
        fontSize,
        fontFamily,
        textContent,
        reRenderRightSidebar
      );
    }
  };

  // Styles
  const selectStyles = {
    control: (base) => ({
      ...base,
      border: "1px solid #ccc",
      borderRadius: "0px",
      boxShadow: "none",
      "&:hover": { border: "1px solid #ccc" },
    }),
    valueContainer: (base) => ({
      ...base,
      fontSize: "16px",
      marginLeft: "4px",
      overflow: "visible",
      padding: "0px 8px",
    }),
    placeholder: (base) => ({ ...base, position: "absolute" }),
  };

  // Fabric Prototypes
  let img = document.createElement("img");
  fabric.Object.prototype.transparentCorners = false;
  fabric.Object.prototype.cornerColor = "#0c5460";
  fabric.Object.prototype.cornerStyle = "circle";
  fabric.Object.prototype.cornerSize = 5;
  fabric.Object.prototype.controls.deleteControl = new fabric.Control({
    x: 0.5, y: -0.5, offsetY: 0, cursorStyle: "pointer",
    mouseUpHandler: deleteObject, render: renderIcon, cornerSize: 25,
  });

  // Effects
  useEffect(() => {
    if (signatureDocumentData) {
      let { width, height } = signatureDocumentData?.dimensions;
      setPdfDimensions({ width, height });
    }
  }, [signatureDocumentData]);

  const [pdfDimensions, setPdfDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!canvas && pdfDimensions.width && pdfDimensions.height) {
      const initCanvas = new fabric.Canvas('canv', {
        height: pdfDimensions.height,
        width: pdfDimensions.width,
        backgroundColor: '#E9ECEF', // Grey background for gaps
      });
      setCanvas(initCanvas);
    } else if (canvas) {
      canvas.setDimensions({
        width: pdfDimensions.width,
        height: pdfDimensions.height,
      });
      canvas.setBackgroundColor('#E9ECEF', canvas.renderAll.bind(canvas));
      canvas.renderAll();
    }
  }, [pdfDimensions, canvas]);

  useEffect(() => {
    const loadFont = async () => {
      try {
        const font = new FontFace('Playball', `url(${process.env.PUBLIC_URL}/fonts/Playball-Regular.ttf)`);
        await font.load();
        document.fonts.add(font);
        setFontLoaded(true);
      } catch (error) {
        console.error('Font load error:', error);
      }
    };
    loadFont();
  }, []);

  function storeMousePosition(x, y) {
    let position = { x: x, y: y };
    localStorage.setItem("mousePosition", JSON.stringify(position));
  }

  // Scroll Handler
  useEffect(() => {
    const { width, height } = pdfDimensions;
    const dynamicHeight = imageList.length * (height + 25);

    const handleScroll = () => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);

      debounceTimeout.current = setTimeout(() => {
        const scrollableDiv = containerRef.current;
        const scrollTop = scrollableDiv.scrollTop;
        const windowHeight = scrollableDiv.clientHeight;
        const scrollHeight = scrollableDiv.scrollHeight;
        const threshold = 100;

        if (windowHeight + scrollTop >= scrollHeight - threshold) {
          const newCanvasHeight = Math.min(dynamicHeight, scrollHeight + height);
          canvas.setDimensions({ width: width, height: newCanvasHeight });
          canvas.renderAll();
        }
      }, 200);
    };

    const scrollableDiv = containerRef.current;
    if (scrollableDiv) scrollableDiv.addEventListener("scroll", handleScroll);

    return () => {
      if (scrollableDiv) scrollableDiv.removeEventListener("scroll", handleScroll);
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    };
  }, [canvas, imageList, pdfDimensions, containerRef]);

  // Render Images
  const renderImageOnCanvas = useCallback(async () => {
    if (makeFillable === true && imageList.length > 0) {
      canvas.getObjects().forEach(obj => canvas.remove(obj));
      canvas.clear();
      canvas.setBackgroundColor('#E9ECEF', canvas.renderAll.bind(canvas));

      var pagePositionArr = [];
      var pdfWidth = pdfDimensions.width;
      var pdfHeight = pdfDimensions.height;

      const spacing = 30;
      const topPadding = 30;
      const totalCanvasHeight = imageList.length * (pdfHeight + spacing) + topPadding;

      canvas.setDimensions({ width: pdfWidth, height: totalCanvasHeight });

      for (var i = 0; i < imageList.length; i++) {
        const topPosition = (i * (pdfHeight + spacing)) + topPadding;
        var imageUrl = imageList[i].file_path;

        await new Promise((resolve) => {
          fabric.Image.fromURL(imageUrl, function (img) {
            const scaleX = pdfWidth / img.width;
            const scaleY = pdfHeight / img.height;
            const scale = Math.min(scaleX, scaleY);

            img.scale(scale);
            const imgWidth = img.width * scale;
            const left = (pdfWidth - imgWidth) / 2;

            img.set({
              left: left,
              top: topPosition,
              hasControls: false,
              selectable: false,
              hoverCursor: "default",
              objectCaching: true,
              shadow: new fabric.Shadow({ color: 'rgba(0,0,0,0.1)', blur: 5, offsetX: 0, offsetY: 2 })
            });

            canvas.add(img).renderAll();
            resolve();
          },
            {
              id: "page_" + (i + 1),
              num: 0,
              left: 0,
              top: topPosition,
              hasControls: false,
              selectable: false,
              hoverCursor: "default",
              objectCaching: true,
            }
          );
        });

        let objArr = {
          object_id: "page_" + (i + 1),
          position: { left: 0, top: topPosition, width: pdfWidth, height: pdfHeight },
        };
        pagePositionArr.push(objArr);
      }

      localStorage.setItem("pagesPosition", JSON.stringify(pagePositionArr));
      setTimeout(() => autoFillEditModeRef.current?.click(), 500);

      canvas.on("dragover", function (options) {
        const pointer = canvas.getPointer(options.e);
        storeMousePosition(pointer.x, pointer.y);
      });
    }
  }, [makeFillable, imageList, canvas, pdfDimensions]);

  useEffect(() => { renderImageOnCanvas(); }, [renderImageOnCanvas, pdfDimensions]);

  // Recipient Logic
  useEffect(() => {
    let row = formObj.sign_documents.find((x) => parseInt(x.id) === parseInt(signDocId));
    if (row !== undefined) {
      let mode = row ? parseInt(row.mode) : 1;
      let recipients = [];

      if (mode === 1) {
        let recipientObj = { ...EVERY_ONE_OBJECT };
        recipientObj.image = generateOptionImage("d1ecf1", "Everyone");
        recipientObj.color_code = "d1ecf1";
        recipients = [recipientObj];
      }

      for (let i = 0; i < formObj.recipient_List.length; i++) {
        let r = formObj.recipient_List[i];
        let fullName = r.first_name + " " + r.last_name;
        let bg = generateColorCode(fullName, 0.5);
        recipients.push({
          value: r.id, label: fullName,
          image: generateOptionImage(bg, r.first_name), color_code: bg
        });
      }
      setDocRecipients(recipients);
    }
  }, [formObj, signDocId]);

  const fillImageList = useCallback(async () => {
    if (signatureDocumentData?.id) {
      setSignDocMode(parseInt(signatureDocumentData?.mode));
      if (parseInt(signatureDocumentData?.mode) === 2) setRequestSign([]);

      getEnvelopeRequestSignDocumentPages({ signature_document_id: signatureDocumentData?.id })
        .then((response) => {
          setSignDocId(signatureDocumentData?.id);
          setImageList(response.data.data);
          setFileName(response.data.file_name);

          if (signatureDocumentData.request_id !== "" && signatureDocumentData.mode === 1) {
            let selectedObj = [];
            let recipients = signatureDocumentData.request_id.split(",");
            for (let i = 0; i < recipients.length; i++) {
              let index = docRecipients.findIndex((x) => parseInt(x.value) === parseInt(recipients[i]));
              if (index > -1) {
                selectedObj.push({
                  value: docRecipients[index]["value"],
                  label: docRecipients[index]["label"],
                });
              }
            }
            setRequestSign(selectedObj);
          }
        }).catch(() => { });
    }
  }, [signatureDocumentData, docRecipients]);

  useEffect(() => {
    if (makeFillable === true) {
      setRequestSign([EVERY_ONE_OBJECT]);
      fillImageList();
      setTimeout(() => setLoading(false), 2000);
    }
  }, [makeFillable, setLoading, fillImageList]);

  useEffect(() => {
    if (signDocMode === 1) setVisibleFabricTools(true);
    else setVisibleFabricTools(false);
    setNoFieldSelectionFlag(true);
  }, [signDocMode]);

  // Helpers
  function generateColorCode(name, opacity) {
    let hash = 5381;
    for (let i = 0; i < name.length; i++) hash = (hash * 33) ^ name.charCodeAt(i);
    const colorCode = (hash >>> 0).toString(16).slice(0, 6).padStart(6, '0');
    const alpha = Math.round(opacity * 255).toString(16).padStart(2, '0');
    return colorCode + alpha;
  }

  function generateOptionImage(background, name) {
    let firstChar = name.charAt(0).toUpperCase() + name.slice(1);
    return `https://ui-avatars.com/api/?name=${firstChar}&background=${background}&color=000&rounded=true&bold=true&uppercase=true&length=2`;
  }

  // --- SAVE FORM ---
  function saveForm(e) {
    e.preventDefault();
    setErrors(errorsObj);
    setLoading(true);

    let placeholdersUpdated = localStorage.getItem("placeholders");
    let placeholders = placeholdersUpdated ? JSON.parse(placeholdersUpdated) : null;

    if (!placeholders || placeholders.length === 0) {
      setLoading(false);
      toast.error("Please add at least one signature field");
      return;
    }

    let pagesPosition = localStorage.getItem("pagesPosition");
    pagesPosition = pagesPosition ? JSON.parse(pagesPosition) : null;

    if (!pagesPosition) {
      setLoading(false);
      toast.error("Document layout information is missing");
      return;
    }

    let recipientValue = [];
    if (signDocMode === 1) {
      if (requestSign.length === 0) {
        setLoading(false);
        toast.error("Please select at least one recipient");
        return;
      }
      if (requestSign.some(r => r.value === 0)) {
        recipientValue = formObj.recipient_List.map(recipient => ({
          value: recipient.id,
          label: `${recipient.first_name} ${recipient.last_name}`
        }));
      } else {
        recipientValue = requestSign;
      }
    } else if (signDocMode === 2) {
      const uniqueRecipients = [...new Set(placeholders.filter(p => !p.is_deleted && p.request_recipient).map(p => p.request_recipient.value))];
      if (uniqueRecipients.length === 0) {
        setLoading(false);
        toast.error("No Collaborator assigned to signature fields");
        return;
      }
      recipientValue = docRecipients.filter(r => uniqueRecipients.includes(r.value));
    }

    let obj = {
      envelope_id: formObj.envelope_id,
      mode: signDocMode,
      signature_document_id: signDocId,
      request_form: recipientValue,
      pages_position: pagesPosition,
      items: placeholders.filter(p => !p.is_deleted),
    };

    signPlaceholdersUpdate(obj)
      .then((response) => {
        let requestDisplay = recipientValue.map(recipient => {
          const rData = formObj.recipient_List.find(r => r.id === recipient.value) || { first_name: "", last_name: "" };
          return {
            full_name: `${rData.first_name} ${rData.last_name}`,
            display: `${rData.first_name.charAt(0)}${rData.last_name.charAt(0)}`
          };
        });

        const updatedForm = { ...formObj };
        const docIndex = updatedForm.sign_documents.findIndex(x => parseInt(x.id) === parseInt(signDocId));
        if (docIndex > -1) {
          updatedForm.sign_documents[docIndex] = {
            ...updatedForm.sign_documents[docIndex],
            request_id: recipientValue.map(r => r.value).join(","),
            request_display: requestDisplay
          };
        }

        setFormObj(updatedForm);
        setLoading(false);
        toast.success(response.data.message);
        clearForm(e);
        clsFormButtonRef.current?.click();

        // Note: Auto summary call removed intentionally
      })
      .catch((err) => {
        setLoading(false);
        toast.error(Utils.getErrorMessage(err));
      });
  }

  function clearForm(e) {
    if (e) e.preventDefault();
    setImageList([]);
    setRequestSign("");
    setVisibleFabricTools(false);
    setNoFieldSelectionFlag(true);
    handleMakeFillable();
    clearLocalStorage();
    canvas.clear();
  }

  function clearLocalStorage() {
    localStorage.removeItem("mousePosition");
    localStorage.removeItem("pagesPosition");
    localStorage.removeItem("placeholders");
  }

  // --- Fabric Event Listeners ---
  function objectMovedListener(ev) {
    if (ev.target.id.indexOf("sign") > -1 || ev.target.id.indexOf("textbox") > -1) {
      mapObjectInPage(ev.target);
    }
  }

  function mapObjectInPage(target) {
    var placeholdersArr = JSON.parse(localStorage.getItem("placeholders"));
    if (!placeholdersArr) return;

    var objIndex = placeholdersArr.findIndex((obj) => obj.object_id === target.id);
    var items = canvas.getObjects().filter((o) => o.id.indexOf("page") > -1);
    var intersectPageObjectId = "";

    for (var i = 0; i < items.length; i++) {
      if (target.intersectsWithObject(items[i])) {
        intersectPageObjectId = items[i].id;
      }
    }

    if (intersectPageObjectId !== "" && objIndex >= 0) {
      placeholdersArr[objIndex].page_object_id = intersectPageObjectId;
      localStorage.setItem("placeholders", JSON.stringify(placeholdersArr));
    }
  }

  function mouseUpListener(ev) {
    let target = ev.target;
    if (target && target.id && (target.id.indexOf("sign") > -1 || target.id.indexOf("textbox") > -1)) {
      storeObjectInLocal(target);
      renderRightSidebar();
    }
  }

  function renderRightSidebar() {
    var activeObject = canvas.getActiveObject();
    if (activeObject !== null) {
      let objectId = activeObject.id;
      setVisibleDateFormatDropdown(objectId.indexOf("date") > -1);
      setVisibleTextContentTextarea(objectId.indexOf("static") > -1);
      setVisibleAssignToDropdown(signDocMode === 2 && objectId.indexOf("static") === -1);

      if (signDocMode === 2 && objectId.indexOf("static") === -1) {
        setAssignToRecipient(activeObject.requestRecipient);
      }

      var placeholdersArr = JSON.parse(localStorage.getItem("placeholders"));
      var objIndex = placeholdersArr.findIndex((obj) => obj.object_id === objectId);

      if (objIndex > -1) {
        var item = placeholdersArr[objIndex];

        let foundFont = FONT_FAMILY_LIST.find(o => o.value === item.text_font_family);
        let foundSize = FONT_SIZE_LIST.find(o => o.value === item.text_font_size);
        let foundDate = DATEFORMAT_LIST.find(o => o.value === item.date_format);

        setSelectedFont(foundFont);
        setSelectedFontSize(foundSize);
        if (foundDate) setSelectedDateFormat(foundDate);
        if (item.text_content) setSelectedTextContent(item.text_content);
      }
      setNoFieldSelectionFlag(false);
    } else {
      setNoFieldSelectionFlag(true);
    }
  }

  function objectAddedListener(ev) {
    if (ev.target.id.indexOf("sign") > -1 || ev.target.id.indexOf("textbox") > -1) {
      storeObjectInLocal(ev.target);
      mapObjectInPage(ev.target);
    }
  }

  function storeObjectInLocal(target) {
    var objectId = target.id;
    var objBox = target.getBoundingRect();
    let colorCode = getBackgroundColor(target);
    let textContent = getLabelFromTextBoxPlaceholder(target);
    let requestRecipient = getRequestRecipient(target);
    let dateFormat = objectId.indexOf("date") > -1 ? "DD/MM/YYYY" : "";

    var placeholdersArr = JSON.parse(localStorage.getItem("placeholders")) || [];
    var objIndex = placeholdersArr.findIndex((obj) => obj.object_id === objectId);

    if (objIndex < 0) {
      placeholdersArr.push({
        object_id: objectId,
        mode: signDocMode,
        request_recipient: requestRecipient,
        position: objBox,
        page_object_id: "",
        text_content: textContent,
        text_font_size: "14",
        text_font_family: 'Arial',
        date_format: dateFormat,
        is_deleted: false,
        color_code: colorCode
      });
    } else {
      placeholdersArr[objIndex].position = objBox;
      placeholdersArr[objIndex].text_content = textContent;
      placeholdersArr[objIndex].request_recipient = requestRecipient;
      placeholdersArr[objIndex].color_code = colorCode;
    }
    localStorage.setItem("placeholders", JSON.stringify(placeholdersArr));
  }

  // --- Getters & Helpers ---
  function getLabelFromTextBoxPlaceholder(target) {
    let objectText = findObjectByKey(target._objects, "text");
    return objectText ? objectText.text : "";
  }
  function getRequestRecipient(target) { return target.requestRecipient || ''; }
  function getBackgroundColor(target) { return target.backgroundColor || ''; }

  function deleteObject(eventData, transform) {
    var target = transform.target;
    updateDeleteFlagInSignObject(target);
    target.canvas.remove(target);
    target.canvas.requestRenderAll();
  }

  function updateDeleteFlagInSignObject(target) {
    var placeholdersArr = JSON.parse(localStorage.getItem("placeholders"));
    var objIndex = placeholdersArr.findIndex((obj) => obj.object_id === target.id);
    if (objIndex > -1) {
      placeholdersArr[objIndex].is_deleted = true;
      placeholdersArr[objIndex].page_object_id = "";
      localStorage.setItem("placeholders", JSON.stringify(placeholdersArr));
    }
  }

  function renderIcon(ctx, left, top, styleOverride, fabricObject) {
    var size = this.cornerSize;
    ctx.save();
    ctx.translate(left, top);
    ctx.rotate(fabric.util.degreesToRadians(fabricObject.angle));
    ctx.drawImage(img, -size / 2, -size / 2, size, size);
    ctx.restore();
  }

  function generateId(type) { return `${type}-${Math.random().toString(36).substr(2, 9)}`; }

  // --- Fabric Object Creation ---
  function createTextLabel(textContent, fontSize, fontFamily, textColor, originX, originY, textLeft, textTop, previewMode) {
    const effectiveFontFamily = (fontFamily === "Playball" && !fontLoaded) ? "Arial" : fontFamily;
    return new fabric.Text(textContent || "", {
      fontSize: fontSize || 14, fontFamily: effectiveFontFamily, fill: textColor || "#000000",
      originX: originX || "left", originY: originY || "top", left: textLeft || 0, top: textTop || 0,
      objectCaching: true,
    });
  }

  function createRectangle(objId, imageWidth, imageHeight, fillColor, previewMode) {
    const rectWidth = Math.max(10, (imageWidth || 150) + 60);
    const rectHeight = Math.max(10, (imageHeight || 50) + 20);
    return new fabric.Rect({
      width: rectWidth, height: rectHeight, radius: 1, objectCaching: true, hasControls: false,
      fill: previewMode ? "#FFFFFF" : (fillColor || "#d1ecf1"),
      stroke: previewMode ? (fillColor || "#d1ecf1") : "#ccc",
      strokeWidth: previewMode ? 3 : 1,
      strokeDashArray: previewMode ? [5, 5] : null,
      selectable: !previewMode
    });
  }

  function createGroup(rect, img, fTextLabel, objId, left, top, previewMode) {
    return new fabric.Group([rect, img, fTextLabel], {
      id: objId, left: left, top: top, lockRotation: true, preserveObjectStacking: true,
      selectable: !previewMode, hoverCursor: previewMode ? "pointer" : "all-scroll", objectCaching: true,
    });
  }

  function createImage(imageURL) {
    return new Promise((resolve, reject) => {
      fabric.Image.fromURL(imageURL, (img) => img ? resolve(img) : reject("Failed to load image"));
    });
  }

  function fabricTool(backgroundColor, top, left, width, height, objId, requestRecipient = "", fontSize, fontFamily, textContent = "", reRenderRightSidebar = true, previewMode = false) {
    let originX = "left", originY = "top", textLeft = 15, textTop = 0, fillColor = "#" + backgroundColor, textColor = "#000000";
    let imageURL = "", textLeftPosition = 35;

    if (!previewMode) {
      if (objId.indexOf("recipient-textbox") > -1) { textContent = "Recipient Name"; imageURL = "/images/recipient-tool-icon.png"; }
      else if (objId.indexOf("system-date-textbox") > -1) { textContent = "Date Signed"; imageURL = "/images/calender-tool-icon.png"; }
      else if (objId.indexOf("initial-textbox") > -1) { textContent = "Initial"; imageURL = "/images/initial-tool-icon.png"; }
      else if (objId.indexOf("email-address-textbox") > -1) { textContent = "Email Address"; imageURL = "/images/email-tool-icon.png"; }
      else if (objId.indexOf("sign") > -1) { textContent = "Signature"; imageURL = "/images/sign-tool-icon.png"; }
      else { textLeftPosition = 10; textContent = textContent || "Add Content"; fillColor = "#d1ecf1"; }

      if (requestRecipient !== "" && objId.indexOf("static") === -1) {
        textContent += " (" + getInitial(requestRecipient.label) + ")";
      }
    } else {
      if (objId.indexOf("static") > -1) { textLeftPosition = 10; textContent = textContent || "Add Content"; fillColor = "#d1ecf1"; }
    }

    var fTextLabel = createTextLabel(textContent, fontSize, fontFamily, textColor, originX, originY, textLeft, textTop, previewMode);

    createImage(imageURL).then((img) => {
      var rect = createRectangle(objId, fTextLabel.width, fTextLabel.height, fillColor, previewMode);
      var scaleFactor = Math.min(20 / img.width, 20 / img.height);
      var newHeight = img.height * scaleFactor;

      img.set({ left: rect.left + 10, top: 1 + (rect.height - newHeight) / 2, scaleX: scaleFactor, scaleY: scaleFactor, opacity: 0.7 });
      fTextLabel.set({ top: (rect.height - fTextLabel.height) / 2, left: previewMode ? 10 : textLeftPosition });

      var group = createGroup(rect, img, fTextLabel, objId, left, top, previewMode);
      group.setControlsVisibility({ mtr: false });
      group.set({ requestRecipient: requestRecipient, backgroundColor: backgroundColor });

      canvas.on("object:moving", objectMovedListener);
      canvas.on("mouse:up", mouseUpListener);
      canvas.on("object:added", objectAddedListener);
      canvas.add(group);

      if (!previewMode) canvas.setActiveObject(group);
      canvas.renderAll();

      if (reRenderRightSidebar) {
        setSelectedFont(FONT_FAMILY_LIST[0]);
        setSelectedFontSize(FONT_SIZE_LIST[0]);
        setSelectedDateFormat(DATEFORMAT_LIST[0]);
        setSelectedTextContent('');
        renderRightSidebar();
      }
    }).catch((e) => console.log(e));
  }

  // --- Auto Fill Edit Mode ---
  function handelAutoFillEditMode() {
    // ... [Logic kept same as original, just streamlined] ... 
    // This logic restores tools from imageList.placeholder_details to canvas
    // Re-using existing logic to ensure items are drawn
    let hasUpdated = false;
    for (var i = 0; i < imageList.length; i++) {
      var pDetails = imageList[i].placeholder_details;
      if (pDetails && pDetails.items) {
        let placeholdersArr = JSON.parse(localStorage.getItem("placeholders")) || [];
        pDetails.items.forEach(item => {
          if (!placeholdersArr.find(obj => obj.object_id === item.object_id)) {
            placeholdersArr.push(item);
            hasUpdated = true;
          }
          // Draw tool
          fabricTool(item.color_code, item.position.top, item.position.left, item.position.width, item.position.height, item.object_id, item.request_recipient, item.text_font_size, item.text_font_family, item.text_content, false);
        });
        if (hasUpdated) localStorage.setItem("placeholders", JSON.stringify(placeholdersArr));
      }
    }
    setVisibleFabricTools(signDocMode === 1);
  }

  function findObjectByKey(objects, keyToFind) {
    return objects.find(obj => obj.hasOwnProperty(keyToFind)) || null;
  }

  // --- Tool Properties Changes ---
  const updateActiveToolProperty = (key, value, updateStateFn) => {
    var activeObject = canvas.getActiveObject();
    if (activeObject) {
      updateStateFn(value);
      var placeholdersArr = JSON.parse(localStorage.getItem("placeholders"));
      var objIndex = placeholdersArr.findIndex((obj) => obj.object_id === activeObject.id);

      if (key === 'fontFamily' || key === 'fontSize' || key === 'text') {
        // Re-render needed for visual changes
        updateDeleteFlagInSignObject(activeObject);
        canvas.remove(activeObject);
        placeholdersArr[objIndex][key === 'text' ? 'text_content' : (key === 'fontSize' ? 'text_font_size' : 'text_font_family')] = (typeof value === 'object' ? value.value : value);
        placeholdersArr[objIndex].is_deleted = false;
        localStorage.setItem("placeholders", JSON.stringify(placeholdersArr));

        let item = placeholdersArr[objIndex];
        fabricTool(item.color_code, item.position.top, item.position.left, item.position.width, item.position.height, item.object_id, item.request_recipient, item.text_font_size, item.text_font_family, item.text_content, false);
      } else {
        // Simple metadata update
        placeholdersArr[objIndex][key] = (typeof value === 'object' ? value.value : value);
        localStorage.setItem("placeholders", JSON.stringify(placeholdersArr));
      }
    } else {
      toast.error("Please select specific tool.");
    }
  };

  function toolFontChange(val) { updateActiveToolProperty('fontFamily', val, setSelectedFont); }
  function toolFontSizeChange(val) { updateActiveToolProperty('fontSize', val, setSelectedFontSize); }
  function toolDateFormatChange(val) { updateActiveToolProperty('date_format', val, setSelectedDateFormat); }
  function toolTextContentChange(e) { updateActiveToolProperty('text', e.target.value, setSelectedTextContent); }

  function changeRecipientOnSingleSignMode(options) {
    if (options.findIndex((x) => parseFloat(x.value) === 0) > -1) setRequestSign([EVERY_ONE_OBJECT]);
    else setRequestSign(options);
  }

  function changeRecipientOnMultiSignMode(options) {
    if (options && options.value) {
      setRequestSign([options]);
      setSignerSelectedBackgroundColor(options.color_code);
      setVisibleFabricTools(true);
      setTempRecipient(options);
    } else toast.error("Please select a valid collaborator.");
  }

  function changeRecipientOnTool(options) {
    setAssignToRecipient(options);
    var activeObject = canvas.getActiveObject();
    if (activeObject) {
      updateDeleteFlagInSignObject(activeObject);
      canvas.remove(activeObject);

      var placeholdersArr = JSON.parse(localStorage.getItem("placeholders"));
      var objIndex = placeholdersArr.findIndex((obj) => obj.object_id === activeObject.id);

      placeholdersArr[objIndex].color_code = options.color_code;
      placeholdersArr[objIndex].request_recipient = options;
      placeholdersArr[objIndex].is_deleted = false;
      localStorage.setItem("placeholders", JSON.stringify(placeholdersArr));

      let item = placeholdersArr[objIndex];
      fabricTool(options.color_code, item.position.top, item.position.left, item.position.width, item.position.height, item.object_id, options, item.text_font_size, item.text_font_family, item.text_content, false);
    }
  }

  function togglePreview() {
    var items = canvas.getObjects().filter((o) => o.id && o.id.indexOf("page") === -1);
    if (items.length === 0) {
      toast.error("Please drag the tools on pdf.");
    } else {
      if (previewButtonText === "Preview") {
        setPreviewButtonText("Turn Off Preview");
        if (containerRef.current) containerRef.current.style.boxShadow = '0 1rem 3rem rgba(0,0,0,.175)';
        renderPreviewTool(true);
        setNoFieldSelectionFlag(true);
      } else {
        if (containerRef.current) containerRef.current.style.boxShadow = 'none';
        renderPreviewTool(false);
        setPreviewButtonText("Preview");
      }
    }
  }

  function renderPreviewTool(previewMode) {
    canvas.getObjects().filter(o => o.id && o.id.indexOf("page") === -1).forEach(o => canvas.remove(o));
    var placeholdersArr = JSON.parse(localStorage.getItem("placeholders"));

    placeholdersArr.forEach(function (item) {
      if (!item.is_deleted) {
        let textContent = item.text_content;
        let recipientId = signDocMode === 1 ? (requestSign[0].value === 0 ? formObj.recipient_List[0].id : requestSign[0].value) : item.request_recipient.value;

        if (recipientId) {
          let rObj = formObj.recipient_List.find(obj => obj.id === recipientId);
          if (rObj) {
            if (item.object_id.indexOf("recipient") > -1) textContent = rObj.first_name + " " + rObj.last_name;
            else if (item.object_id.indexOf("system-date") > -1) textContent = Utils.getCurrentDateByFormat(item.date_format);
            else if (item.object_id.indexOf("initial") > -1) textContent = getInitial(rObj.first_name + " " + rObj.last_name);
            else if (item.object_id.indexOf("email") > -1) textContent = rObj.email;
            else if (item.object_id.indexOf("sign") > -1) textContent = rObj.first_name + " " + rObj.last_name;
          }
        }
        fabricTool(item.color_code, item.position.top, item.position.left, item.position.width, item.position.height, item.object_id, item.request_recipient, item.text_font_size, item.text_font_family, textContent, false, previewMode);
      }
    });
  }

  function deleteTool() {
    var activeObject = canvas.getActiveObject();
    if (activeObject) {
      updateDeleteFlagInSignObject(activeObject);
      canvas.remove(activeObject);
      canvas.requestRenderAll();
      setNoFieldSelectionFlag(true);
    } else toast.error("Please select specific tool.");
  }

  function getInitial(name) {
    if (!name) return "";
    const parts = name.split(" ").filter((part) => part.trim());
    return parts.length === 1 ? parts[0].slice(0, 2) : `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`;
  }

  const formatOptionLabel = ({ label, image }) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <img src={image} alt={label} style={{ width: '30px', height: '30px', marginRight: '10px' }} />
      <span>{label}</span>
    </div>
  );

  return (
    <div className="offcanvas offcanvas-end makeFillableForm" ref={offcanvasRef} data-bs-scroll="true" data-bs-backdrop="static" data-bs-keyboard="false" tabIndex="-1" id="makeFillableForm" aria-labelledby="makeFillableFormLabel">
      <div className="make-fillable-header">
        <h5 className="make-fillable-title" id="makeFillableFormLabel"><i className="bi bi-file-earmark-text"></i> Contract Signing Tool</h5>
        <button type="button" ref={clsFormButtonRef} className="bg-transparent" data-bs-dismiss="offcanvas" onClick={clearForm} aria-label="Close"><i className="bi bi-x-circle" aria-hidden="true" /></button>
      </div>
      <div className="d-none" onClick={handelAutoFillEditMode} ref={autoFillEditModeRef}></div>

      <div className="make-fillable-body">
        {/* Left Tools */}
        <div className="tools-sidebar">
          <div className="tools-content">
            {(signDocMode === 1 || signDocMode === 2) && (
              <div className="mb-3">
                <h6 className="tool-section-title"><i className="bi bi-people-fill"></i> Signers</h6>
                <Select closeMenuOnSelect={true} value={requestSign} components={animatedComponents} isMulti={signDocMode === 1}
                  onChange={signDocMode === 1 ? changeRecipientOnSingleSignMode : changeRecipientOnMultiSignMode}
                  options={docRecipients} styles={selectStyles} placeholder="Select signers..." className="react-select-container" classNamePrefix="react-select" />
                {requestSign.length === 0 && <div className="text-muted small mt-2 d-flex align-items-start gap-2"><i className="bi bi-info-circle mt-1"></i><span>Select a signer to enable tools</span></div>}
                {errors.recipients && <div className="text-danger small mt-2">{errors.recipients}</div>}
              </div>
            )}

            {visibleFabricTools && (
              <div className="mb-3">
                <h6 className="tool-section-title"><i className="bi bi-pen-fill"></i> Signature Fields</h6>
                <div className="d-flex flex-column gap-2">
                  {['sign', 'initial-textbox'].map((type) => (
                    <button key={type} draggable={requestSign.length > 0} onDragStart={(e) => requestSign.length > 0 ? handleDragStart(e, type) : e.preventDefault()} onDragEnd={handleDragStop} className="tool-btn" disabled={requestSign.length === 0}>
                      <div className="tool-icon-wrapper"><img src={`/images/${type === 'sign' ? 'sign-tool-icon' : 'initial-tool-icon'}.png`} alt={type} /></div>
                      <span>{type === 'sign' ? 'Signature' : 'Initial'}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {visibleFabricTools && (
              <div className="mb-3">
                <h6 className="tool-section-title"><i className="bi bi-lightning-charge-fill"></i> Auto-fill Fields</h6>
                <div className="d-flex flex-column gap-2">
                  {[{ type: 'system-date-textbox', label: 'Date Signed', icon: 'calender-tool-icon' }, { type: 'recipient-textbox', label: 'Recipient Name', icon: 'recipient-tool-icon' }, { type: 'email-address-textbox', label: 'Email Address', icon: 'email-tool-icon' }].map((field) => (
                    <button key={field.type} draggable={requestSign.length > 0} onDragStart={(e) => requestSign.length > 0 ? handleDragStart(e, field.type) : e.preventDefault()} onDragEnd={handleDragStop} className="tool-btn" disabled={requestSign.length === 0}>
                      <div className="tool-icon-wrapper"><img src={`/images/${field.icon}.png`} alt={field.label} /></div><span>{field.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-2">
              <div draggable onDragStart={(e) => handleDragStart(e, "static-textbox")} onDragEnd={handleDragStop} className="tool-btn justify-content-center text-center" style={{ borderStyle: 'dashed' }} data-toggle="tooltip" title="Add Static Content">
                <span className="fw-bold text-primary"><i className="bi bi-plus-lg me-2"></i> ADD CONTENT</span>
              </div>
            </div>
          </div>
        </div>

        {/* Canvas */}
        <div className="canvas-wrapper">
          <div className="canvas-container-card" ref={containerRef}>
            <div id="canvasContainer" className="p-0">
              <canvas id="canv" ref={canvasRef} style={{ border: "none" }} />
            </div>
          </div>
          <div className="action-bar">
            {/* ENABLED SUMMARY BUTTON */}
            <ContractSummaryModal
              envelopeId={formObj.envelope_id}
              fileName={fileName || (formObj.sign_documents && formObj.sign_documents.length > 0 ? formObj.sign_documents[formObj.sign_documents.length - 1].file_name : null)}
              container={offcanvasRef.current}
            />
            <button type="button" onClick={togglePreview} className="btn btn-outline-primary"><i className={`bi bi-eye${previewButtonText === 'Preview' ? '' : '-slash'} me-2`}></i>{previewButtonText}</button>
            <button ref={clsFormButtonRef} type="button" data-bs-dismiss="offcanvas" onClick={clearForm} className="btn btn-outline-secondary">Cancel</button>
            <button type="button" onClick={saveForm} className="btn btn-primary">Save</button>
          </div>
        </div>

        {/* Properties */}
        <div className="properties-sidebar">
          {noFieldSelectionFlag ? (
            <div className="empty-state">
              <img src="/images/signpen.webp" alt="No selection" />
              <h5 className="fw-bold text-dark mb-2">{signDocMode === 1 ? 'No Selection' : 'No Signer'}</h5>
              <p className="small">{signDocMode === 1 ? 'Click on a field in the document to edit its properties.' : 'Please select a signer from the left panel.'}</p>
            </div>
          ) : (
            <div className="w-100">
              <div className="d-flex align-items-center justify-content-between mb-2 pb-3 border-bottom">
                <h6 className="fw-bold m-0">Properties</h6>
                <button onClick={deleteTool} className="btn btn-link text-danger p-0 text-decoration-none small fw-bold"><i className="bi bi-trash3 me-1"></i> Remove</button>
              </div>
              {visibleAssignToDropdown && (
                <div className="property-group"><label className="property-label">Assigned To</label><Select closeMenuOnSelect={true} value={assignToRecipient} onChange={changeRecipientOnTool} options={docRecipients} styles={selectStyles} formatOptionLabel={formatOptionLabel} className="react-select-container" classNamePrefix="react-select" /></div>
              )}
              <div className="property-group">
                <label className="property-label mb-3">Typography</label>
                <div className="d-flex flex-column gap-3">
                  <div><label className="small text-dark mb-1 fw-bolder">Font Family</label><Select closeMenuOnSelect={true} value={selectedFont} components={animatedComponents} onChange={toolFontChange} options={FONT_FAMILY_LIST} styles={selectStyles} className="react-select-container" classNamePrefix="react-select" /></div>
                  <div><label className="small text-dark mb-1 fw-bolder">Font Size</label><Select closeMenuOnSelect={true} value={selectedFontSize} components={animatedComponents} onChange={toolFontSizeChange} options={FONT_SIZE_LIST} styles={selectStyles} className="react-select-container" classNamePrefix="react-select" /></div>
                </div>
              </div>
              {visibleDateFormatDropdown && <div className="property-group"><label className="property-label">Date Format</label><Select closeMenuOnSelect={true} value={selectedDateFormat} components={animatedComponents} onChange={toolDateFormatChange} options={DATEFORMAT_LIST} styles={selectStyles} className="react-select-container" classNamePrefix="react-select" /></div>}
              {visibleTextContentTextarea && <div className="property-group"><label className="property-label">Content</label><textarea className="custom-textarea" value={selectedTextContent} onChange={toolTextContentChange} rows={4} placeholder="Type here..." /></div>}
              {Array.isArray(docRecipients) && docRecipients.length > 1 && assignToRecipient && assignToRecipient.label && <div className="mt-4 p-3 bg-light rounded border"><div className="small text-muted mb-1">Current Signer</div><div className="fw-semibold text-dark">{assignToRecipient.label}</div></div>}
            </div>
          )}
        </div>
      </div>


    </div>
  );
}

export default MakeFillablePopup;